import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError, api } from '../../api/client';
import { AuthLayout } from './AuthLayout';
import { IconInfo } from '../../shared/icons';

interface ForcedPasswordChangeProps {
  /** Called once the new password is saved, so the app reloads the account. */
  onChanged: () => void;
  onSignOut: () => void;
}

/**
 * Shown in place of the app until the person replaces the temporary password
 * an administrator gave them. Nothing else is reachable from here: the server
 * refuses every other endpoint while the flag is set, so this screen is a
 * courtesy to the person, not the only line of defence.
 */
export function ForcedPasswordChange({ onChanged, onSignOut }: ForcedPasswordChangeProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const change = useMutation({
    mutationFn: () => api.changePassword(currentPassword, newPassword),
    onSuccess: onChanged,
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    change.mutate();
  };

  const error = change.error instanceof ApiError ? change.error : null;

  return (
    <AuthLayout kicker="Action required" heading="Choose your own password">
      <form
        className="auth-card"
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        onSubmit={handleSubmit}
      >
        <p className="field-help" style={{ fontSize: 11, lineHeight: '17px', margin: 0 }}>
          You signed in with a temporary password. Enter it once more as the current password,
          then choose a password only you know. The temporary one stops working as soon as you
          save.
        </p>

        {error && (
          <div className="notice notice-error" role="alert">
            <IconInfo size={14} />
            <span>{error.message}</span>
          </div>
        )}
        {mismatch && (
          <div className="notice notice-error" role="alert">
            <IconInfo size={14} />
            <span>The two new passwords do not match.</span>
          </div>
        )}

        <div className="field">
          <label className="lbl" htmlFor="forced-current">
            <span className="lbl-text">Temporary password</span>
          </label>
          <input
            id="forced-current"
            className="inp"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="lbl" htmlFor="forced-new">
            <span className="lbl-text">New password</span>
          </label>
          <input
            id="forced-new"
            className="inp"
            type="password"
            autoComplete="new-password"
            minLength={12}
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <span className="field-help">At least 12 characters.</span>
        </div>

        <div className="field">
          <label className="lbl" htmlFor="forced-confirm">
            <span className="lbl-text">Confirm new password</span>
          </label>
          <input
            id="forced-confirm"
            className={`inp${mismatch ? ' inp-invalid' : ''}`}
            type="password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>

        <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={change.isPending}>
          {change.isPending ? 'Saving…' : 'Save and continue'}
        </button>

        <button className="link-button" type="button" onClick={onSignOut} style={{ alignSelf: 'center' }}>
          Sign out instead
        </button>
      </form>
    </AuthLayout>
  );
}
